import Grid from '@mui/material/Grid';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  changeFilterGroupBy,
  findSalesRequested,
  reportFilterAppliedSelector,
  reportFilterCountryCodeSelector,
  reportFilterGroupBySelector,
  reportSalesLoadingSelector,
  reportFilterStartAccountingDateSelector,
  reportFilterEndAccountingDateSelector,
  reportFilterStartBusinessDateSelector,
  reportFilterEndBusinessDateSelector,
  reportFilterStartOperationDateSelector,
  reportFilterEndOperationDateSelector,
} from '../state';

import {
  AccountingDateRangeFilter,
  BusinessDateRangeFilter,
  CountryFilter,
  HourRangeFilter,
  OperationDateRangeFilter,
  PodFilter,
  PosFilter,
  ReportActionsGridItem,
  ReportFilterActions,
  ReportFilters,
  ReportFiltersProps,
  SalesTypesFilter,
  SegmentsFilter,
  StoreFilter,
  TimeBandFilter,
} from './ReportFilters';

import { AppPermissions, ReportFilterGroupBy } from '../types';
import { PermissionsProvider } from './Permissions';
import { constants } from '../helpers';
import { FilterPanel, StatusChip } from './Visual';

const groupByConfig: ReportFiltersProps<ReportFilterGroupBy>['groupByConfig'] = {
  options: {
    countryAcronym: true,
    storeAcronym: false,
    podPod: false,
    businessDate: false,
    accountingDate: false,
    operationDate: false,
    posNum: false,
    saleType: false,
    timeBand: false,
    timeRange: false,
    posType: false,
    regionalMgmtId: false,
    regionId: false,
    mgmtId: false,
    supervisionId: false,
    agreementId: false,
    companyId: false,
  },
  action: changeFilterGroupBy,
  selector: reportFilterGroupBySelector,
};

const dialogFiltersConfig: ReportFiltersProps<ReportFilterGroupBy>['dialogFiltersConfig'] = {
  options: ['startOperationDate', 'endOperationDate', 'startBusinessDate', 'endBusinessDate'],
  selector: reportFilterAppliedSelector,
  filters: (
    <>
      <BusinessDateRangeFilter maxDaysBetween={constants.reportsFilterLimitDateRange} />
      <OperationDateRangeFilter maxDaysBetween={constants.reportsFilterLimitDateRange} />
    </>
  ),
};

const onedriveAction = () => findSalesRequested(true);

const GridBr = () => {
  const countryCode = useSelector(reportFilterCountryCodeSelector);

  if (countryCode !== 'BR') return null;

  return (
    <Grid item xs={12} md={3} lg={2} xl={2}>
      <PodFilter />
    </Grid>
  );
};

const SalesTotalsSearch = () => {
  const { t } = useTranslation();
  const startBusinessDate = useSelector(reportFilterStartBusinessDateSelector);
  const endBusinessDate = useSelector(reportFilterEndBusinessDateSelector);
  const startAccountingDate = useSelector(reportFilterStartAccountingDateSelector);
  const endAccountingDate = useSelector(reportFilterEndAccountingDateSelector);
  const startOperationDate = useSelector(reportFilterStartOperationDateSelector);
  const endOperationDate = useSelector(reportFilterEndOperationDateSelector);

  const hasCountry = Boolean(useSelector(reportFilterCountryCodeSelector));
  const hasAnyDateRange =
    (startBusinessDate.accept && endBusinessDate.accept) ||
    (startAccountingDate.accept && endAccountingDate.accept) ||
    (startOperationDate.accept && endOperationDate.accept);
  const applyDisabled = !(hasAnyDateRange && hasCountry);
  const countryRequiredIncomplete = applyDisabled && !hasCountry;
  const dateRangeRequiredIncomplete = applyDisabled && !hasAnyDateRange;

  return (
    <ReportFilters
      groupByConfig={groupByConfig}
      dialogFiltersConfig={dialogFiltersConfig}
      loadingSelector={reportSalesLoadingSelector}
      applyAction={findSalesRequested}
      onedriveAction={onedriveAction}
      showOpStructureFilters
    >
      <PermissionsProvider resourcePermissions={[AppPermissions.SALES_TOTALS]}>
        <FilterPanel
          subtitleTranslationKey="sale-totals.filters.subtitle"
          actions={
            <StatusChip
              status={applyDisabled ? 'warning' : 'success'}
              label={
                applyDisabled ? t('filters.status.missingRequired') : t('filters.status.ready')
              }
            />
          }
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3} lg={2} xl={2}>
              <CountryFilter fullWidth requiredIncomplete={countryRequiredIncomplete} />
            </Grid>
            <Grid item xs={12} md={3} lg={2} xl={2}>
              <StoreFilter />
            </Grid>
            <GridBr />
            <Grid item xs={12} md={3} lg={2} xl={2}>
              <PosFilter />
            </Grid>
            <Grid item xs={12} md={4} lg={4} xl={2}>
              <SalesTypesFilter />
            </Grid>
            <Grid item xs={12} md={4} lg={2} xl={2}>
              <SegmentsFilter />
            </Grid>
            <Grid item xs={12} md={4} lg={2} xl={2}>
              <TimeBandFilter />
            </Grid>
            <Grid item xs={12} md={4} lg={2} xl={2}>
              <HourRangeFilter />
            </Grid>
            <Grid item xs={12} md={6} lg={4} xl={3}>
              <AccountingDateRangeFilter
                spacing={1}
                maxDaysBetween={constants.reportsFilterLimitDateRange}
                requiredIncomplete={dateRangeRequiredIncomplete}
              />
            </Grid>
            <ReportActionsGridItem>
              <ReportFilterActions applyDisabled={applyDisabled} />
            </ReportActionsGridItem>
          </Grid>
        </FilterPanel>
      </PermissionsProvider>
    </ReportFilters>
  );
};

export default SalesTotalsSearch;
